import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Context Providers
import { AppProvider } from './contexts/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import TournamentsPage from './pages/public/TournamentsPage';
import TournamentDetailPage from './pages/public/TournamentDetailPage';
import ArticlesPage from './pages/public/ArticlesPage';
import ArticleDetailPage from './pages/public/ArticleDetailPage';
import PostDetailPage from './pages/public/PostDetailPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import SignupParentPage from './pages/auth/SignupParentPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ParentDashboardPage from './pages/parent/ParentDashboardPage';
// import OTPVerificationPage from './pages/auth/OTPVerificationPage'; // Not using OTP for now
import CompleteSignupPage from './pages/auth/CompleteSignupPage';
import OnboardingSportsPage from './pages/auth/OnboardingSportsPage';
import OnboardingSlidesPage from './pages/auth/OnboardingSlidesPage';

// Admin Components
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import UserProfile from './pages/admin/UserProfile';
import ModerationPage from './pages/admin/ModerationPage';
import AdminCommunities from './pages/admin/AdminCommunities';
import CommunityDetail from './pages/admin/CommunityDetail';
import AdminTournaments from './pages/admin/AdminTournaments';
import TournamentDetail from './pages/admin/TournamentDetail';
import AdminAchievements from './pages/admin/AdminAchievements';
import AdminSettings from './pages/admin/AdminSettings';
import AdminPostsPage from './pages/admin/AdminPostsPage';

// User Components
import UserLayout from './components/user/UserLayout';
import HomePage from './pages/user/HomePage';
import SearchPage from './pages/user/SearchPage';
import CommunityPage from './pages/user/CommunityPage';
import ProfilePage from './pages/user/ProfilePage';
import ChatsPage from './pages/user/ChatsPage';
import WallOfFamePage from './pages/user/WallOfFamePage';
import FollowRequestsPage from './pages/user/FollowRequestsPage';

// Demo Components
import MediaDemo from './pages/demo/MediaDemo';

// Protected Route Component
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import ScrollToTop from './components/common/ScrollToTop';
import ImpersonationBanner from './components/common/ImpersonationBanner';

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppProvider>
        <AuthProvider>
          <SocketProvider>
            <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
            <div className="App">
          <ImpersonationBanner />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/tournaments" element={<TournamentsPage />} />
            <Route path="/tournaments/:id" element={<TournamentDetailPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:slug" element={<ArticleDetailPage />} />
            <Route path="/posts/:id" element={<PostDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/parent" element={<SignupParentPage />} />
            <Route path="/parent" element={
              <ProtectedRoute role="user">
                <ParentDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* <Route path="/verify-otp" element={<OTPVerificationPage />} /> */}
            <Route path="/complete-signup" element={<CompleteSignupPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <HomePage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/onboarding/sports" element={<OnboardingSportsPage />} />
            <Route path="/onboarding/slides" element={<OnboardingSlidesPage />} />
            
            {/* Demo Routes */}
            <Route path="/demo/media" element={<MediaDemo />} />

            {/* Admin Routes */}
            <Route path="/admin/*" element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <Routes>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="users/:id" element={<UserProfile />} />
                    <Route path="communities" element={<AdminCommunities />} />
                    <Route path="communities/:id" element={<CommunityDetail />} />
                    <Route path="tournaments" element={<AdminTournaments />} />
                    <Route path="tournaments/:id" element={<TournamentDetail />} />
                    <Route path="achievements" element={<AdminAchievements />} />
                    <Route path="moderation" element={<ModerationPage />} />
                    <Route path="posts" element={<AdminPostsPage />} />
                    <Route path="reports" element={<div>Reports Page - Coming Soon</div>} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            } />

            {/* User Routes */}
            <Route path="/home" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <HomePage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/search" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <SearchPage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/community" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <CommunityPage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile/:id" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <ProfilePage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/chats" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <ChatsPage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/chats/:chatId" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <ChatsPage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/wall-of-fame" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <WallOfFamePage />
                </UserLayout>
              </ProtectedRoute>
            } />
            <Route path="/follow-requests" element={
              <ProtectedRoute role="user">
                <UserLayout>
                  <FollowRequestsPage />
                </UserLayout>
              </ProtectedRoute>
            } />
          </Routes>

          {/* Global Toast Notifications */}
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
            </div>
          </ErrorBoundary>
          </SocketProvider>
        </AuthProvider>
      </AppProvider>
    </Router>
  );
}

export default App;